import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Search as SearchIcon } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { getProducts, searchProducts } from '../services/productService';

const Search = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const { formatMoney, getProductPrice } = useCurrency();
  const { wishlistIds, toggleWishlist } = useWishlist();

  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const all = await getProducts();
        setAllProducts(Array.isArray(all) ? all : []);
      } catch {
        setAllProducts([]);
      }
    };
    load();
  }, []);

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchProducts(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const suggestionTerms = useMemo(() => {
    const term = String(searchQuery || '').trim().toLowerCase();
    if (!term) return [];

    const set = new Set();
    for (const p of allProducts) {
      const name = String(p?.name || '').trim();
      if (name && name.toLowerCase().includes(term)) set.add(name);

      const category = String(p?.category || '').trim();
      if (category && category.toLowerCase().includes(term)) set.add(category);

      const categories = Array.isArray(p?.categories) ? p.categories : [];
      for (const c of categories) {
        const v = String(c || '').trim();
        if (v && v.toLowerCase().includes(term)) set.add(v);
      }

      const sizes = Array.isArray(p?.sizes) ? p.sizes : [];
      for (const s of sizes) {
        const v = String(s || '').trim();
        if (v && v.toLowerCase().includes(term)) set.add(v);
      }

      const colors = Array.isArray(p?.colors) ? p.colors : [];
      for (const c of colors) {
        const v = String(c || '').trim();
        if (v && v.toLowerCase().includes(term)) set.add(v);
      }
    }

    return Array.from(set.values()).slice(0, 6);
  }, [allProducts, searchQuery]);

  const onToggleWishlist = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleWishlist(productId);
    } catch {
      // ignore
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-medium tracking-wide mb-8">SEARCH</h1>

        {/* Search Input */}
        <div className="relative mb-8">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search for products..."
            className="w-full border border-gray-300 px-4 py-4 pr-12 text-sm focus:outline-none focus:border-black transition-colors"
            autoFocus
          />
          <SearchIcon className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />

          {suggestionTerms.length > 0 ? (
            <div className="absolute left-0 right-0 top-full bg-white border border-gray-200 shadow-lg z-10">
              {suggestionTerms.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleSearch(t)}
                  className="block w-full text-left px-4 py-3 text-sm hover:bg-gray-100 transition-colors"
                >
                  {t}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Search Results */}
        {searchQuery && (
          <div>
            <p className="text-sm text-gray-600 mb-6">
              {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} for "{searchQuery}"
            </p>

            {searchResults.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No products found</p>
                <div className="flex flex-wrap gap-2 justify-center mb-6">
                  {['New arrivals', 'Tees', 'Outerwear', 'Black', 'S', 'M'].map((term) => (
                    <button
                      key={term}
                      onClick={() => handleSearch(term)}
                      className="border border-gray-300 px-4 py-2 text-sm hover:border-black transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
                <Link
                  to="/shop/new-arrivals"
                  className="text-sm underline hover:opacity-60"
                >
                  Browse all products
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {searchResults.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    className="group"
                  >
                    <div className="aspect-square bg-gray-100 mb-3 overflow-hidden relative">
                      <img
                        src={product.images?.[0]}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <button
                        onClick={(e) => onToggleWishlist(e, product.id)}
                        className={`absolute top-2 right-2 w-9 h-9 flex items-center justify-center border transition-colors ${
                          wishlistIds.includes(String(product.id))
                            ? 'border-black bg-black text-white'
                            : 'border-gray-200 bg-white/90 hover:bg-white hover:border-black'
                        }`}
                        aria-label="Toggle wishlist"
                      >
                        <Heart size={16} />
                      </button>
                    </div>
                    <h3 className="text-sm font-medium mb-1">{product.name}</h3>
                    <p className="text-sm">{formatMoney(getProductPrice(product))}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Popular Searches */}
        {!searchQuery && (
          <div>
            <h2 className="text-sm font-medium mb-4">POPULAR SEARCHES</h2>
            <div className="flex flex-wrap gap-2">
              {['Hoodies', 'Tees', 'Denim', 'Jackets', 'Pants'].map(term => (
                <button
                  key={term}
                  onClick={() => handleSearch(term)}
                  className="border border-gray-300 px-4 py-2 text-sm hover:border-black transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;